import React, { useState, useEffect, useRef } from 'react';
import { Heart, ListMusic, Share2, X, Plus, Package } from 'lucide-react';
import WishlistStar from './WishlistStar';
import ShareToWallModal from './ShareToWallModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * IdResultActions — action bar for the Identify-a-Track result card.
 *
 * Actions:
 *  🔍 Find Similar Tracks — opens the existing Similar Tracks modal with this track as source
 *  ❤️ Favorite (toggle)
 *  ⭐ Wishlist (toggle via WishlistStar)
 *  ➕ Add to Playlist (opens picker)
 *  📦 Add to Crate (DJ Crate Box — opens picker)
 */
const IdResultActions = ({ track, onFindSimilar }) => {
  const [isFav, setIsFav] = useState(false);
  const [favId, setFavId] = useState(null);
  const [favLoading, setFavLoading] = useState(false);
  // Playlist picker
  const [showPlaylistPicker, setShowPlaylistPicker] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [playlistLoading, setPlaylistLoading] = useState(false);
  const [addingToPlaylistId, setAddingToPlaylistId] = useState(null);
  const [pickerSuccess, setPickerSuccess] = useState(null);
  // Crate picker
  const [showCratePicker, setShowCratePicker] = useState(false);
  const [crates, setCrates] = useState([]);
  const [cratesLoading, setCratesLoading] = useState(false);
  const [addingToCrateId, setAddingToCrateId] = useState(null);
  const [crateSuccess, setCrateSuccess] = useState(null);
  // Share-to-Wall modal
  const [showShareToWall, setShowShareToWall] = useState(false);
  const pickerRef = useRef(null);

  // Current user (read from localStorage; mirrors how other features read auth)
  const currentUser = (() => {
    try {
      const raw = localStorage.getItem('beatseek_user');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  })();

  // Stable identifier we use to talk to the backend
  const trackId =
    track?.id ||
    track?.track_id ||
    `${track?.artist || ''}-${track?.title || ''}`.toLowerCase().replace(/\s+/g, '-');

  // Check favorite status on mount
  useEffect(() => {
    let cancelled = false;
    async function checkFav() {
      if (!trackId) return;
      try {
        const token = localStorage.getItem('beatseek_token');
        const res = await fetch(`${API}/api/users/favorites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data.favorites) ? data.favorites : [];
        const match = list.find(
          (f) =>
            (f.track?.id || f.track_id) === trackId ||
            (f.track?.title === track?.title && f.track?.artist === track?.artist)
        );
        if (!cancelled) {
          setIsFav(!!match);
          setFavId(match?.id || null);
        }
      } catch (e) { /* silent */ }
    }
    checkFav();
    return () => { cancelled = true; };
  }, [trackId, track?.title, track?.artist]);

  const handleFavToggle = async () => {
    if (favLoading) return;
    setFavLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      if (isFav) {
        await fetch(`${API}/api/favorites/${trackId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsFav(false);
        setFavId(null);
      } else {
        const body = {
          track: {
            id: trackId,
            title: track?.title || '',
            artist: track?.artist || '',
            album: track?.album || '',
            artwork_url: track?.artwork || track?.artwork_url || '',
            source: track?.source || 'BEATSEEK ID',
            bpm: track?.bpm || null,
            key: track?.key || null,
          },
        };
        const res = await fetch(`${API}/api/favorites`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const data = await res.json();
          setIsFav(true);
          setFavId(data.id);
        }
      }
    } finally {
      setFavLoading(false);
    }
  };

  const openPlaylistPicker = async () => {
    setShowPlaylistPicker(true);
    setPlaylistLoading(true);
    setPickerSuccess(null);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${API}/api/playlists/my-playlists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPlaylists(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setPlaylists([]);
    } finally {
      setPlaylistLoading(false);
    }
  };

  const openCratePicker = async () => {
    setShowCratePicker(true);
    setCratesLoading(true);
    setCrateSuccess(null);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${API}/api/crates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Crates endpoint may return array or { crates: [...] }
        const list = Array.isArray(data) ? data : (data.crates || []);
        setCrates(list);
      }
    } catch (e) {
      setCrates([]);
    } finally {
      setCratesLoading(false);
    }
  };

  const addToCrate = async (crate) => {
    if (addingToCrateId) return;
    setAddingToCrateId(crate.id);
    try {
      const token = localStorage.getItem('beatseek_token');
      const body = {
        track_id: trackId,
        source: (track?.source || 'beatseek_id').toLowerCase().replace(/\s+/g, '_'),
        title: track?.title || '',
        artist: track?.artist || '',
        album: track?.album || null,
        bpm: typeof track?.bpm === 'number' ? track.bpm : null,
        artwork_url: track?.artwork || track?.artwork_url || null,
        preview_url: track?.preview_url || null,
      };
      const res = await fetch(`${API}/api/crates/${crate.id}/tracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setCrateSuccess(`✓ Added to "${crate.name}"`);
        setTimeout(() => {
          setShowCratePicker(false);
          setCrateSuccess(null);
        }, 1200);
      } else {
        const err = await res.json().catch(() => ({}));
        setCrateSuccess(`✗ ${err.detail || 'Could not add to crate'}`);
        setTimeout(() => setCrateSuccess(null), 2400);
      }
    } catch (e) {
      setCrateSuccess('✗ Network error');
      setTimeout(() => setCrateSuccess(null), 2400);
    } finally {
      setAddingToCrateId(null);
    }
  };

  const addToPlaylist = async (playlist) => {
    if (addingToPlaylistId) return;
    setAddingToPlaylistId(playlist.id);
    try {
      const token = localStorage.getItem('beatseek_token');
      const body = {
        track_id: trackId,
        title: track?.title || '',
        artist: track?.artist || '',
        artwork_url: track?.artwork || track?.artwork_url || '',
        source: track?.source || 'BEATSEEK ID',
        local_id: track?.local_id || null,
      };
      const res = await fetch(`${API}/api/playlists/${playlist.id}/tracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setPickerSuccess(`✓ Added to "${playlist.name}"`);
        setTimeout(() => {
          setShowPlaylistPicker(false);
          setPickerSuccess(null);
        }, 1200);
      } else {
        const err = await res.json().catch(() => ({}));
        setPickerSuccess(`✗ ${err.detail || 'Could not add'}`);
        setTimeout(() => setPickerSuccess(null), 2200);
      }
    } catch (e) {
      setPickerSuccess('✗ Network error');
      setTimeout(() => setPickerSuccess(null), 2200);
    } finally {
      setAddingToPlaylistId(null);
    }
  };

  const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    width: 34,
    height: 34,
    padding: 0,
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 9,
    cursor: 'pointer',
    transition: 'transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
    border: '1px solid transparent',
    color: '#fff',
    letterSpacing: '0.02em',
  };

  return (
    <>
      <div
        data-testid="id-result-actions"
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 12,
          paddingTop: 10,
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <button
          onClick={handleFavToggle}
          disabled={favLoading}
          data-testid="id-favorite-btn"
          aria-label={isFav ? 'Remove from Favorites' : 'Add to Favorites'}
          style={{
            ...btnBase,
            background: isFav ? 'rgba(239, 68, 68, 0.85)' : 'rgba(239, 68, 68, 0.12)',
            border: `1px solid ${isFav ? '#ef4444' : 'rgba(239, 68, 68, 0.4)'}`,
            color: isFav ? '#fff' : '#fca5a5',
            opacity: favLoading ? 0.6 : 1,
          }}
          title={isFav ? 'Remove from Favorites' : 'Add to Favorites'}
        >
          <Heart size={15} fill={isFav ? '#fff' : 'none'} />
        </button>

        <button
          onClick={openCratePicker}
          data-testid="id-add-crate-btn"
          aria-label="Add to a DJ Crate"
          style={{
            ...btnBase,
            background: 'rgba(0, 255, 255, 0.12)',
            border: '1px solid rgba(0, 255, 255, 0.5)',
            color: '#67e8f9',
          }}
          title="Add to a DJ Crate"
        >
          <Package size={15} />
        </button>

        <button
          onClick={openPlaylistPicker}
          data-testid="id-add-playlist-btn"
          aria-label="Add to a BEATSEEK playlist"
          style={{
            ...btnBase,
            background: 'rgba(168, 85, 247, 0.15)',
            border: '1px solid rgba(168, 85, 247, 0.45)',
            color: '#d8b4fe',
          }}
          title="Add to a BEATSEEK playlist"
        >
          <ListMusic size={15} />
        </button>

        <button
          onClick={() => setShowShareToWall(true)}
          data-testid="id-share-to-wall-btn"
          aria-label="Share to The Wall"
          style={{
            ...btnBase,
            background: 'rgba(34, 211, 238, 0.12)',
            border: '1px solid rgba(34, 211, 238, 0.45)',
            color: '#67e8f9',
          }}
          title="Share to The Wall"
        >
          <Share2 size={15} />
        </button>

        <div
          style={{
            ...btnBase,
            background: 'rgba(255, 215, 0, 0.10)',
            border: '1px solid rgba(255, 215, 0, 0.40)',
            color: '#facc15',
            cursor: 'default',
          }}
          title="Wishlist"
        >
          <WishlistStar
            itemType="track"
            itemData={{
              id: trackId,
              title: track?.title || '',
              artist: track?.artist || '',
              artwork_url: track?.artwork || track?.artwork_url || '',
              bpm: track?.bpm || null,
              key: track?.key || null,
            }}
            size={15}
          />
        </div>
      </div>

      {/* Playlist Picker — compact inline modal */}
      {showPlaylistPicker && (
        <div
          onClick={() => setShowPlaylistPicker(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.78)',
            zIndex: 100001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
          data-testid="id-playlist-picker-overlay"
        >
          <div
            ref={pickerRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 460,
              maxHeight: '78vh',
              background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1e 100%)',
              border: '1px solid rgba(168, 85, 247, 0.4)',
              borderRadius: 16,
              boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(168, 85, 247, 0.18)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ListMusic size={20} style={{ color: '#c084fc' }} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#fff' }}>
                  Add to Playlist
                </h3>
              </div>
              <button
                onClick={() => setShowPlaylistPicker(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.55)',
                  cursor: 'pointer',
                  padding: 4,
                }}
                data-testid="id-playlist-picker-close"
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '10px 14px', flex: 1, overflowY: 'auto' }}>
              {playlistLoading ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                  Loading playlists…
                </div>
              ) : playlists.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'rgba(255,255,255,0.55)' }}>
                  <ListMusic size={36} style={{ opacity: 0.3, marginBottom: 10 }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No playlists yet.</p>
                  <p style={{ margin: '4px 0 0', fontSize: 11, opacity: 0.6 }}>
                    Create one in BEATSEEK first.
                  </p>
                </div>
              ) : (
                playlists.map((pl) => (
                  <button
                    key={pl.id}
                    onClick={() => addToPlaylist(pl)}
                    disabled={!!addingToPlaylistId}
                    data-testid={`id-playlist-pick-${pl.id}`}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      marginBottom: 6,
                      background: addingToPlaylistId === pl.id
                        ? 'rgba(168, 85, 247, 0.18)'
                        : 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 10,
                      cursor: addingToPlaylistId ? 'wait' : 'pointer',
                      transition: 'background 160ms ease, border-color 160ms ease',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (!addingToPlaylistId) {
                        e.currentTarget.style.background = 'rgba(168, 85, 247, 0.12)';
                        e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.35)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!addingToPlaylistId) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                      }
                    }}
                  >
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 6,
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(6,182,212,0.18))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {pl.artwork_url || pl.cover_url ? (
                        <img
                          src={pl.artwork_url || pl.cover_url}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }}
                        />
                      ) : (
                        <ListMusic size={18} style={{ color: '#c084fc' }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {pl.name}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>
                        {pl.tracks?.length || 0} / 10 tracks
                      </div>
                    </div>
                    <Plus size={16} style={{ color: '#c084fc', flexShrink: 0 }} />
                  </button>
                ))
              )}
            </div>
            {pickerSuccess && (
              <div
                style={{
                  padding: '10px 14px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  background: pickerSuccess.startsWith('✓')
                    ? 'rgba(34, 197, 94, 0.18)'
                    : 'rgba(239, 68, 68, 0.18)',
                  color: pickerSuccess.startsWith('✓') ? '#4ade80' : '#fca5a5',
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: 'center',
                }}
                data-testid="id-playlist-picker-toast"
              >
                {pickerSuccess}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Crate Picker — DJ Crate Box add-to */}
      {showCratePicker && (
        <div
          onClick={() => setShowCratePicker(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.78)',
            zIndex: 100001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
          data-testid="id-crate-picker-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 460,
              maxHeight: '78vh',
              background: 'linear-gradient(180deg, #0a1622 0%, #0a0e1a 100%)',
              border: '1px solid rgba(0, 255, 255, 0.5)',
              borderRadius: 16,
              boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(0, 255, 255, 0.22)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid rgba(0, 255, 255, 0.18)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Package size={20} style={{ color: '#22d3ee' }} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#fff' }}>
                  Add to DJ Crate
                </h3>
              </div>
              <button
                onClick={() => setShowCratePicker(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.55)',
                  cursor: 'pointer',
                  padding: 4,
                }}
                data-testid="id-crate-picker-close"
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '10px 14px', flex: 1, overflowY: 'auto' }}>
              {cratesLoading ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                  Loading crates…
                </div>
              ) : crates.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'rgba(255,255,255,0.55)' }}>
                  <Package size={36} style={{ opacity: 0.3, marginBottom: 10 }} />
                  <p style={{ margin: 0, fontSize: 13 }}>No crates yet.</p>
                  <p style={{ margin: '4px 0 0', fontSize: 11, opacity: 0.6 }}>
                    Open the DJ Crate Box to create one.
                  </p>
                </div>
              ) : (
                crates.map((cr) => (
                  <button
                    key={cr.id}
                    onClick={() => addToCrate(cr)}
                    disabled={!!addingToCrateId}
                    data-testid={`id-crate-pick-${cr.id}`}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      marginBottom: 6,
                      background: addingToCrateId === cr.id
                        ? 'rgba(0, 255, 255, 0.16)'
                        : 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 10,
                      cursor: addingToCrateId ? 'wait' : 'pointer',
                      transition: 'background 160ms ease, border-color 160ms ease',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (!addingToCrateId) {
                        e.currentTarget.style.background = 'rgba(0, 255, 255, 0.10)';
                        e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!addingToCrateId) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                      }
                    }}
                  >
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 6,
                        background: 'linear-gradient(135deg, rgba(0,255,255,0.18), rgba(34,211,238,0.18))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Package size={18} style={{ color: '#22d3ee' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {cr.name || 'Untitled crate'}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>
                        {cr.track_count ?? (cr.tracks ? cr.tracks.length : 0)} tracks
                        {cr.bpm_min && cr.bpm_max ? ` · ${cr.bpm_min}-${cr.bpm_max} BPM` : ''}
                      </div>
                    </div>
                    <Plus size={16} style={{ color: '#22d3ee', flexShrink: 0 }} />
                  </button>
                ))
              )}
            </div>
            {crateSuccess && (
              <div
                style={{
                  padding: '10px 14px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  background: crateSuccess.startsWith('✓')
                    ? 'rgba(34, 197, 94, 0.18)'
                    : 'rgba(239, 68, 68, 0.18)',
                  color: crateSuccess.startsWith('✓') ? '#4ade80' : '#fca5a5',
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: 'center',
                }}
                data-testid="id-crate-picker-toast"
              >
                {crateSuccess}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Share to The Wall modal (Phase 2) */}
      <ShareToWallModal
        isOpen={showShareToWall}
        onClose={() => setShowShareToWall(false)}
        source="id"
        track={track}
        currentUser={currentUser}
      />
    </>
  );
};

export default IdResultActions;
